import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import * as XLSX from "xlsx";

const BASE = "https://dailydish.in/api/admin/campaign-blast";
const HUB_BASE = "https://dailydish.in/api/Hub";

// ── tiny style helpers (same conventions as NudgeCampaignAdmin.jsx) ──
const card = {
  background: "#fff",
  borderRadius: 14,
  boxShadow: "0 2px 12px #0001",
  padding: 24,
  marginBottom: 24,
};
const lbl = {
  fontWeight: 600,
  fontSize: 12,
  marginBottom: 3,
  display: "block",
  color: "#555",
};
const inp = {
  width: "100%",
  padding: "8px 11px",
  borderRadius: 8,
  border: "1px solid #ddd",
  fontSize: 13,
  marginBottom: 10,
  boxSizing: "border-box",
};
const btn = (color = "#1e4a1e", extra = {}) => ({
  background: color,
  color: "#fff",
  border: "none",
  borderRadius: 8,
  padding: "8px 18px",
  fontWeight: 600,
  fontSize: 13,
  cursor: "pointer",
  marginRight: 8,
  ...extra,
});

const TEMPLATES = [
  {
    key: "orderedonce01",
    label: "orderedonce01",
    description: "Registered + 1 or 2 total orders ever",
    color: "#1565c0",
  },
  {
    key: "registeredfinal",
    label: "registeredfinal",
    description: "Registered + 0 orders ever",
    color: "#6a1b9a",
  },
];

const CampaignBlastAdmin = () => {
  const [hubs, setHubs] = useState([]);
  const hubById = useMemo(() => {
    const m = new Map();
    hubs.forEach((h) => m.set(String(h._id), h));
    return m;
  }, [hubs]);

  const [templateName, setTemplateName] = useState("orderedonce01");
  const [hubIds, setHubIds] = useState([]);
  const [var2, setVar2] = useState("");
  const [var3, setVar3] = useState("");

  const [preview, setPreview] = useState(null);
  const [checking, setChecking] = useState(false);
  const [sending, setSending] = useState(false);

  const [analytics, setAnalytics] = useState({
    summary: {},
    logs: [],
    hubsInLogs: [],
  });
  // Narrows which template sections are shown. Applied client-side, because the
  // response always carries both segments so each can render its own table.
  const [analyticsFilter, setAnalyticsFilter] = useState("");
  // Applied server-side, so the summary cards and the tables below them always
  // describe the same set of sends.
  const [analyticsHubId, setAnalyticsHubId] = useState("");
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  const [deletingId, setDeletingId] = useState(null);

  const [toast, setToast] = useState("");
  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3500);
  };

  const loadHubs = useCallback(async () => {
    try {
      const r = await axios.get(`${HUB_BASE}/hubs`);
      setHubs(r.data || []);
    } catch {
      showToast("❌ Failed to load hubs");
    }
  }, []);

  // Only the hub goes to the server. The template split is done here so both
  // segments can be shown side by side from one response.
  //
  // requestSeq guards against out-of-order responses: switching the hub
  // filter quickly can make an earlier request resolve after a later one,
  // which would otherwise overwrite the newer hub's data with stale results.
  //
  // The previous hub's rows are also cleared up front (not left on screen
  // until the new response lands) — otherwise switching hubs looks like the
  // filter did nothing while the request is still in flight.
  const requestSeq = useRef(0);
  const loadAnalytics = useCallback(async (hubId) => {
    const seq = ++requestSeq.current;
    setLoadingAnalytics(true);
    setAnalytics({ summary: {}, logs: [], hubsInLogs: [] });
    try {
      const r = await axios.get(`${BASE}/analytics`, {
        params: hubId ? { hubId } : {},
      });
      if (seq !== requestSeq.current) return; // a newer request has since started
      setAnalytics({
        summary: r.data?.summary || {},
        logs: r.data?.logs || [],
        hubsInLogs: r.data?.hubsInLogs || [],
      });
    } catch {
      if (seq !== requestSeq.current) return;
      showToast("❌ Failed to load analytics");
    } finally {
      if (seq === requestSeq.current) setLoadingAnalytics(false);
    }
  }, []);

  // Deletes a single send-log row (e.g. a bad test send). Removed from state
  // directly on success instead of a full refetch — the summary cards are
  // adjusted by hand too, since they'd otherwise go stale against the table.
  const deleteLog = async (log) => {
    if (!window.confirm("Delete this send record? This cannot be undone.")) return;
    setDeletingId(log._id);
    try {
      await axios.delete(`${BASE}/log/${log._id}`);
      setAnalytics((prev) => {
        const s = prev.summary?.[log.templateName];
        const summary = s
          ? {
              ...prev.summary,
              [log.templateName]: {
                ...s,
                totalSent: Math.max(0, s.totalSent - 1),
                delivered: Math.max(0, s.delivered - (log.deliverySuccess ? 1 : 0)),
                clickedCook: Math.max(0, s.clickedCook - (log.clickedButton === "cook" ? 1 : 0)),
                clickedTiming: Math.max(0, s.clickedTiming - (log.clickedButton === "timing" ? 1 : 0)),
              },
            }
          : prev.summary;
        return {
          ...prev,
          summary,
          logs: prev.logs.filter((l) => l._id !== log._id),
        };
      });
      showToast("✅ Deleted");
    } catch (err) {
      showToast(err?.response?.data?.error || "❌ Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  // Exports exactly what's currently on screen — respects both the hub
  // filter (server-side) and the template filter (client-side).
  const exportToExcel = () => {
    const workbook = XLSX.utils.book_new();
    visibleTemplates.forEach((t) => {
      const rows = logsByTemplate[t.key] || [];
      const sheetData = rows.map((l, idx) => ({
        "Sl No": idx + 1,
        Name: l.name || "",
        Phone: l.phone,
        Hub: l.hubName || "",
        Sent: l.deliverySuccess ? "Sent" : "Failed",
        Clicked:
          l.clickedButton === "cook"
            ? "I have a cook"
            : l.clickedButton === "timing"
              ? "Order timing not matching"
              : "",
        "Clicked At": l.clickedAt ? new Date(l.clickedAt).toLocaleString() : "",
      }));
      const worksheet = XLSX.utils.json_to_sheet(sheetData);
      XLSX.utils.book_append_sheet(workbook, worksheet, t.label);
    });
    XLSX.writeFile(
      workbook,
      `CampaignBlast_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  };

  useEffect(() => {
    loadHubs();
    loadAnalytics("");
  }, [loadHubs, loadAnalytics]);

  // Rows split by segment, so each template gets its own table instead of one
  // mixed list where the only difference is a "Template" column.
  const logsByTemplate = useMemo(() => {
    const grouped = {};
    TEMPLATES.forEach((t) => {
      grouped[t.key] = [];
    });
    (analytics.logs || []).forEach((log) => {
      if (!grouped[log.templateName]) grouped[log.templateName] = [];
      grouped[log.templateName].push(log);
    });
    return grouped;
  }, [analytics.logs]);

  const visibleTemplates = analyticsFilter
    ? TEMPLATES.filter((t) => t.key === analyticsFilter)
    : TEMPLATES;

  const toggleHub = (hubId) => {
    setHubIds((ids) =>
      ids.includes(hubId) ? ids.filter((h) => h !== hubId) : [...ids, hubId],
    );
    setPreview(null);
  };

  const checkPreview = async () => {
    setChecking(true);
    try {
      const r = await axios.post(`${BASE}/preview-count`, {
        templateName,
        hubIds,
      });
      setPreview(r.data.count);
    } catch {
      showToast("❌ Could not compute recipient count");
    } finally {
      setChecking(false);
    }
  };

  const send = async () => {
    if (
      !window.confirm(
        `Send "${templateName}" to ${preview != null ? preview : "the matching"} recipient(s)? This sends real WhatsApp messages.`,
      )
    )
      return;
    setSending(true);
    try {
      const r = await axios.post(`${BASE}/send`, {
        templateName,
        hubIds,
        var2,
        var3,
      });
      showToast(`✅ ${r.data.message || "Send started"}`);
      setTimeout(() => loadAnalytics(analyticsFilter), 4000);
    } catch (err) {
      showToast(err?.response?.data?.error || "❌ Send failed");
    } finally {
      setSending(false);
    }
  };

  const hubLabel = (h) =>
    `${h.hubName}${h.hubType ? ` (${h.hubType})` : " — untagged"}`;

  const activeTpl = TEMPLATES.find((t) => t.key === templateName);

  return (
    <div style={{ background: "#f5f5f5", minHeight: "100vh", padding: 24 }}>
      {toast && (
        <div
          style={{
            position: "fixed",
            top: 20,
            right: 20,
            zIndex: 9999,
            background: toast.startsWith("✅")
              ? "#2e7d32"
              : toast.startsWith("❌")
                ? "#c62828"
                : "#1565c0",
            color: "#fff",
            padding: "12px 22px",
            borderRadius: 10,
            fontWeight: 600,
            fontSize: 14,
            boxShadow: "0 4px 16px #0003",
          }}
        >
          {toast}
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontWeight: 800, color: "#1e4a1e", margin: 0 }}>
          📊 Campaign Blast — orderedonce01 / registeredfinal
        </h2>
        <p style={{ color: "#888", margin: "4px 0 0", fontSize: 13 }}>
          Pick a hub set and template, fill in the two custom lines, preview the
          recipient count, then send. Button clicks ("I have a cook" / "Order
          timing not matching") are tracked per recipient below.
        </p>
      </div>

      {/* ── Send form ── */}
      <div style={card}>
        <h5 style={{ fontWeight: 700, marginBottom: 4, color: "#1e4a1e" }}>
          📤 Send a Blast
        </h5>

        <label style={lbl}>Template</label>
        <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
          {TEMPLATES.map((t) => (
            <label
              key={t.key}
              style={{
                flex: 1,
                border: `2px solid ${templateName === t.key ? t.color : "#eee"}`,
                borderRadius: 10,
                padding: 12,
                cursor: "pointer",
                background: templateName === t.key ? `${t.color}11` : "#fff",
              }}
            >
              <input
                type="radio"
                name="templateName"
                checked={templateName === t.key}
                onChange={() => {
                  setTemplateName(t.key);
                  setPreview(null);
                }}
                style={{ marginRight: 8 }}
              />
              <strong style={{ color: t.color }}>{t.label}</strong>
              <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                {t.description}
              </div>
            </label>
          ))}
        </div>

        <label style={lbl}>
          Hubs ({hubIds.length === 0 ? "all hubs" : `${hubIds.length} selected`}
          )
        </label>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            padding: 12,
            border: "1px solid #eee",
            borderRadius: 8,
            marginBottom: 16,
            maxHeight: 160,
            overflowY: "auto",
            background: "#fafafa",
          }}
        >
          {hubs.length === 0 && (
            <span style={{ color: "#aaa", fontSize: 13 }}>Loading hubs…</span>
          )}
          {hubs.map((h) => (
            <label
              key={h._id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
                background: hubIds.includes(String(h._id)) ? "#e8f5e9" : "#fff",
                border: "1px solid #ddd",
                borderRadius: 6,
                padding: "4px 8px",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={hubIds.includes(String(h._id))}
                onChange={() => toggleHub(String(h._id))}
              />
              {hubLabel(h)}
            </label>
          ))}
        </div>

        <div
          style={{
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            marginBottom: 12,
          }}
        >
          <div style={{ flex: 1, minWidth: 220 }}>
            <label style={lbl}>Variable 2 ({"{{2}}"})</label>
            <input
              type="text"
              value={var2}
              onChange={(e) => setVar2(e.target.value)}
              style={inp}
              placeholder="Same text for every recipient in this send"
            />
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <label style={lbl}>Variable 3 ({"{{3}}"})</label>
            <input
              type="text"
              value={var3}
              onChange={(e) => setVar3(e.target.value)}
              style={inp}
              placeholder="Same text for every recipient in this send"
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <button
            onClick={checkPreview}
            disabled={checking}
            style={btn("#1565c0")}
          >
            {checking ? "Checking…" : "👥 Preview Recipient Count"}
          </button>
          {preview !== null && (
            <span style={{ fontSize: 13, fontWeight: 700, color: "#1565c0" }}>
              {preview} user{preview === 1 ? "" : "s"} will receive this message
            </span>
          )}
          <button
            onClick={send}
            disabled={sending || preview === null}
            style={btn(activeTpl.color)}
            title={preview === null ? "Preview the recipient count first" : ""}
          >
            {sending ? "Sending…" : "🚀 Send Now"}
          </button>
        </div>
      </div>

      {/* ── Analytics ── */}
      <div style={card}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 14,
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          <h5 style={{ fontWeight: 700, margin: 0, color: "#1e4a1e" }}>
            📈 Click Analytics
          </h5>
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {/* Hub — refetches, because the server applies it to both the
                summary cards and the tables so the two always agree. Sourced
                from the full hub list (not analytics.hubsInLogs), so a hub
                with zero sends so far is still selectable. */}
            <select
              value={analyticsHubId}
              onChange={(e) => {
                setAnalyticsHubId(e.target.value);
                loadAnalytics(e.target.value);
              }}
              style={{ ...inp, width: 220, marginBottom: 0 }}
            >
              <option value="">All hubs</option>
              {hubs.map((h) => (
                <option key={h._id} value={String(h._id)}>
                  {hubLabel(h)}
                </option>
              ))}
            </select>

            {/* Template — purely which sections to show; no refetch needed. */}
            <select
              value={analyticsFilter}
              onChange={(e) => setAnalyticsFilter(e.target.value)}
              style={{ ...inp, width: 220, marginBottom: 0 }}
            >
              <option value="">Both templates</option>
              {TEMPLATES.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.label}
                </option>
              ))}
            </select>

            <button
              onClick={() => loadAnalytics(analyticsHubId)}
              disabled={loadingAnalytics}
              style={btn("#555")}
            >
              {loadingAnalytics ? "Loading…" : "🔄 Refresh"}
            </button>

            <button
              onClick={exportToExcel}
              disabled={loadingAnalytics || (analytics.logs || []).length === 0}
              style={btn("#1e7e34")}
            >
              📥 Export to Excel
            </button>
          </div>
        </div>

        {/* Summary cards */}
        <div
          style={{
            display: "flex",
            gap: 14,
            marginBottom: 20,
            flexWrap: "wrap",
          }}
        >
          {TEMPLATES.map((t) => {
            const s = analytics.summary?.[t.key] || {};
            return (
              <div
                key={t.key}
                style={{
                  background: "#fff",
                  borderRadius: 12,
                  padding: "14px 20px",
                  boxShadow: "0 2px 8px #0001",
                  borderLeft: `4px solid ${t.color}`,
                  minWidth: 220,
                  flex: 1,
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 700, color: t.color }}>
                  {t.label}
                </div>
                <div style={{ fontSize: 12, color: "#888", marginBottom: 6 }}>
                  Sent: <strong>{s.totalSent ?? 0}</strong> · Delivered:{" "}
                  <strong>{s.delivered ?? 0}</strong>
                </div>
                <div style={{ fontSize: 12, color: "#555" }}>
                  🍳 "I have a cook": <strong>{s.clickedCook ?? 0}</strong>
                </div>
                <div style={{ fontSize: 12, color: "#555" }}>
                  ⏰ "Order timing not matching":{" "}
                  <strong>{s.clickedTiming ?? 0}</strong>
                </div>
              </div>
            );
          })}
        </div>

        {/* One table per template.
            Previously both segments shared a single list whose only
            distinguishing feature was a "Template" column, which made it hard
            to read either segment's response on its own. */}
        {visibleTemplates.map((t) => {
          const rows = logsByTemplate[t.key] || [];
          const stat = analytics.summary?.[t.key] || {};
          const clicks = (stat.clickedCook ?? 0) + (stat.clickedTiming ?? 0);
          return (
            <div key={t.key} style={{ marginBottom: 28 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 8,
                  paddingBottom: 6,
                  borderBottom: `2px solid ${t.color}33`,
                  flexWrap: "wrap",
                }}
              >
                <span style={{ fontWeight: 700, fontSize: 14, color: t.color }}>
                  {t.label}
                </span>
                <span style={{ fontSize: 12, color: "#888" }}>
                  {t.description}
                </span>
                <span
                  style={{
                    marginLeft: "auto",
                    fontSize: 12,
                    color: "#555",
                    whiteSpace: "nowrap",
                  }}
                >
                  {rows.length} sent · {clicks} clicked
                </span>
              </div>

              {rows.length === 0 ? (
                <div
                  style={{
                    color: "#aaa",
                    textAlign: "center",
                    padding: 18,
                    border: "1px dashed #ddd",
                    borderRadius: 10,
                    fontSize: 13,
                  }}
                >
                  No sends yet for this template
                  {analyticsHubId ? " in the selected hub" : ""}
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 13,
                    }}
                  >
                    <thead>
                      <tr style={{ background: "#f0f7ee" }}>
                        {[
                          "Name",
                          "Phone",
                          "Hub",
                          "Sent",
                          "Clicked",
                          "Clicked At",
                          "",
                        ].map((h) => (
                          <th
                            key={h}
                            style={{
                              padding: "10px 12px",
                              textAlign: "left",
                              fontWeight: 600,
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((l) => (
                        <tr
                          key={l._id}
                          style={{ borderBottom: "1px solid #f0f0f0" }}
                        >
                          <td style={{ padding: "8px 12px" }}>
                            {l.name || "—"}
                          </td>
                          <td style={{ padding: "8px 12px" }}>{l.phone}</td>
                          <td style={{ padding: "8px 12px" }}>
                            {l.hubName || "—"}
                          </td>
                          <td style={{ padding: "8px 12px" }}>
                            {l.deliverySuccess ? (
                              <span
                                style={{ color: "#2e7d32", fontWeight: 700 }}
                                title="Accepted by AiSensy"
                              >
                                ✅
                              </span>
                            ) : (
                              <span
                                style={{ color: "#c62828", fontWeight: 700 }}
                                title={l.aisensyError || "Send failed"}
                              >
                                ❌
                              </span>
                            )}
                          </td>
                          <td style={{ padding: "8px 12px" }}>
                            {l.clickedButton === "cook" && "🍳 I have a cook"}
                            {l.clickedButton === "timing" &&
                              "⏰ Order timing not matching"}
                            {!l.clickedButton && (
                              <span style={{ color: "#aaa" }}>—</span>
                            )}
                          </td>
                          <td
                            style={{
                              padding: "8px 12px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {l.clickedAt
                              ? new Date(l.clickedAt).toLocaleString()
                              : "—"}
                          </td>
                          <td style={{ padding: "8px 12px" }}>
                            <button
                              onClick={() => deleteLog(l)}
                              disabled={deletingId === l._id}
                              style={btn("#c62828", {
                                padding: "4px 10px",
                                fontSize: 12,
                                marginRight: 0,
                              })}
                            >
                              {deletingId === l._id ? "…" : "🗑️ Delete"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CampaignBlastAdmin;
